/**
 * Campaign Edit Page - Professional Redesign
 *
 * Full-page editing experience with modern, compact UI.
 * All options visible and easy to configure.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { campaignService } from '../../services/campaigns';
import type { Campaign, UpdateCampaignData } from '../../types/campaign';
import { CampaignFormRedesigned } from '../../components/campaigns/CampaignFormRedesigned';
import { Badge } from '../../components/ui/Badge';
import { useAuthStore } from '../../store/authStore';

export default function CampaignEditPage() {
  const { t } = useTranslation('campaigns');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Get domain limit from user's subscription plan
  const maxDomains = user?.account?.subscription_plan?.domains_per_campaign || 1;

  // Fetch campaign
  useEffect(() => {
    const fetchCampaign = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);
        const data = await campaignService.getCampaign(parseInt(id, 10));
        setCampaign(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('messages.load_failed'));
      } finally {
        setLoading(false);
      }
    };

    fetchCampaign();
  }, [id, t]);

  // Update campaign
  const handleUpdate = async (data: UpdateCampaignData) => {
    if (!id) return;

    try {
      setIsSaving(true);
      const updated = await campaignService.updateCampaign(parseInt(id, 10), data);
      setCampaign(updated);
      toast.success(t('messages.updated_success'));
      navigate(`/campaigns/${id}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('messages.update_failed'));
    } finally {
      setIsSaving(false);
    }
  };

  // Get status badge variant
  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-3">
          <div className="inline-flex items-center justify-center w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-600">Loading campaign...</p>
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="max-w-2xl mx-auto mt-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="text-4xl mb-3">⚠️</div>
          <h3 className="text-lg font-semibold text-red-900 mb-2">Failed to Load Campaign</h3>
          <p className="text-sm text-red-700 mb-4">{error || t('messages.not_found')}</p>
          <button
            onClick={() => navigate('/campaigns')}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
          >
            Back to Campaigns
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        {/* Breadcrumbs */}
        <nav className="flex items-center space-x-2 text-sm mb-3">
          <Link to="/campaigns" className="text-gray-500 hover:text-gray-700">
            {t('edit.breadcrumb_campaigns')}
          </Link>
          <span className="text-gray-400">/</span>
          <Link to={`/campaigns/${campaign.id}`} className="text-gray-500 hover:text-gray-700">
            {campaign.name}
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900 font-medium">{t('edit.breadcrumb_edit')}</span>
        </nav>

        {/* Title and status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{t('edit.title')}</h1>
            <Badge variant={getStatusVariant(campaign.status)}>
              {t(`status.${campaign.status}`)}
            </Badge>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to={`/statistics?campaignId=${campaign.id}`}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <span>📊</span>
              <span>{t('edit.statistics')}</span>
            </Link>
            <Link
              to={`/campaigns/${campaign.id}`}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <span>←</span>
              <span>{t('edit.back')}</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div>

        {/* Redesigned Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <CampaignFormRedesigned
            campaign={campaign}
            onSubmit={handleUpdate}
            onCancel={() => navigate(`/campaigns/${campaign.id}`)}
            isLoading={isSaving}
            maxDomains={maxDomains}
          />
        </div>
      </div>
    </div>
  );
}
